/**
 * ServiceQualityAudit
 *
 * Data completeness audit for service entities, checking:
 * - duration_minutes present
 * - group_size_min / group_size_max present
 * - languages array non-empty
 * - price_model set
 * - price_from_cents present when price_model is 'from' or 'fixed'
 * - inclusions non-empty
 * - exclusions non-empty
 * - cancellation_policy present
 * - location present
 * - Overall completeness score per service
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import {
  AlertCircle,
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCw,
  Search,
  BarChart3,
  ClipboardList,
} from 'lucide-react';
import { useSite } from '@/contexts/SiteContext';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';

interface ServiceEntity {
  id: string;
  name: string;
  slug?: string;
  duration_minutes?: number | null;
  group_size_min?: number | null;
  group_size_max?: number | null;
  languages?: string[] | null;
  price_model?: string | null;
  price_from_cents?: number | null;
  inclusions?: string[] | null;
  exclusions?: string[] | null;
  cancellation_policy?: string | null;
  location?: string | null;
  created_at?: string;
}

interface FieldCheck {
  field: string;
  label: string;
  present: boolean;
}

interface ServiceAuditResult {
  service: ServiceEntity;
  checks: FieldCheck[];
  completeness: number; // 0-100
  presentCount: number;
  totalChecks: number;
}

interface FieldStat {
  field: string;
  label: string;
  present: number;
  total: number;
  rate: number;
}

function auditService(service: ServiceEntity): ServiceAuditResult {
  const checks: FieldCheck[] = [
    {
      field: 'duration_minutes',
      label: 'Duration',
      present: service.duration_minutes != null && service.duration_minutes > 0,
    },
    {
      field: 'group_size_min',
      label: 'Group Size Min',
      present: service.group_size_min != null,
    },
    {
      field: 'group_size_max',
      label: 'Group Size Max',
      present: service.group_size_max != null,
    },
    {
      field: 'languages',
      label: 'Languages',
      present: Array.isArray(service.languages) && service.languages.length > 0,
    },
    {
      field: 'price_model',
      label: 'Price Model',
      present: Boolean(service.price_model),
    },
    {
      field: 'price_from_cents',
      label: 'Price (when applicable)',
      present: service.price_model === 'from' || service.price_model === 'fixed'
        ? service.price_from_cents != null && service.price_from_cents > 0
        : true, // Not applicable for other models
    },
    {
      field: 'inclusions',
      label: 'Inclusions',
      present: Array.isArray(service.inclusions) && service.inclusions.length > 0,
    },
    {
      field: 'exclusions',
      label: 'Exclusions',
      present: Array.isArray(service.exclusions) && service.exclusions.length > 0,
    },
    {
      field: 'cancellation_policy',
      label: 'Cancellation Policy',
      present: Boolean(service.cancellation_policy),
    },
    {
      field: 'location',
      label: 'Location',
      present: Boolean(service.location),
    },
  ];

  const presentCount = checks.filter(c => c.present).length;
  const totalChecks = checks.length;

  return {
    service,
    checks,
    completeness: Math.round((presentCount / totalChecks) * 100),
    presentCount,
    totalChecks,
  };
}

export function ServiceQualityAudit() {
  const { selectedSite } = useSite();
  const [services, setServices] = useState<ServiceEntity[]>([]);
  const [auditResults, setAuditResults] = useState<ServiceAuditResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState<{ total: number; complete: number } | null>(null);

  const fetchServices = useCallback(async () => {
    if (!selectedSite?.id) return;

    setLoading(true);
    setError(null);
    try {
      // Try stats endpoint first for summary
      try {
        const statsResponse = await authFetch(
          `${API_URL}/services/stats/${selectedSite.id}`
        );
        if (statsResponse.ok) {
          const statsData = await statsResponse.json();
          if (statsData.total !== undefined) {
            setStats({ total: statsData.total, complete: statsData.complete || 0 });
          }
        }
      } catch {
        // Stats endpoint optional, continue
      }

      // Fetch full service list
      const response = await authFetch(
        `${API_URL}/services/${selectedSite.id}`
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch services: ${response.status}`);
      }
      const data = await response.json();
      const items: ServiceEntity[] = data.services || data.data || data || [];
      setServices(items);

      // Run audit on all services, sort by completeness (worst first)
      const results = items.map(auditService);
      results.sort((a, b) => a.completeness - b.completeness);
      setAuditResults(results);

      // Compute stats from audit if not from API
      if (!stats) {
        setStats({
          total: results.length,
          complete: results.filter(r => r.completeness === 100).length,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch services');
    } finally {
      setLoading(false);
    }
  }, [selectedSite?.id]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  // Filter results
  const filteredResults = auditResults.filter(r => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return r.service.name.toLowerCase().includes(q);
    }
    return true;
  });

  // Compute per-field stats from all results
  const fieldStats: FieldStat[] = auditResults.length > 0
    ? auditResults[0].checks.map(check => {
        const present = auditResults.filter(r =>
          r.checks.find(c => c.field === check.field)?.present
        ).length;
        return {
          field: check.field,
          label: check.label,
          present,
          total: auditResults.length,
          rate: auditResults.length > 0 ? Math.round((present / auditResults.length) * 100) : 0,
        };
      })
    : [];

  const avgCompleteness = auditResults.length > 0
    ? Math.round(auditResults.reduce((sum, r) => sum + r.completeness, 0) / auditResults.length)
    : 0;

  if (!selectedSite) {
    return (
      <div className="p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Please select a site to view service quality audit.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Service Data Completeness</h1>
          <p className="text-muted-foreground">
            Audit service entities for missing fields to prioritize enrichment
          </p>
        </div>
        <Button variant="outline" onClick={fetchServices} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Services</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{auditResults.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Fully Complete</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {auditResults.filter(r => r.completeness === 100).length}
            </div>
            <p className="text-xs text-muted-foreground">
              {auditResults.length > 0
                ? `${Math.round((auditResults.filter(r => r.completeness === 100).length / auditResults.length) * 100)}%`
                : '0%'} of total
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Avg Completeness</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{avgCompleteness}%</div>
            <Progress value={avgCompleteness} className="h-2 mt-2" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Needs Enrichment</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {auditResults.filter(r => r.completeness < 100).length}
            </div>
            <p className="text-xs text-muted-foreground">
              services with missing data
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Per-field completeness bar chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Per-Field Completeness
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {fieldStats.map(({ field, label, present, total, rate }) => (
            <div key={field} className="flex items-center gap-4">
              <div className="w-44 text-sm font-medium truncate">{label}</div>
              <div className="flex-1">
                <Progress
                  value={rate}
                  className={`h-3 ${rate >= 80 ? '' : rate >= 50 ? '' : ''}`}
                />
              </div>
              <div className="w-28 text-sm text-right text-muted-foreground">
                {present}/{total} ({rate}%)
              </div>
            </div>
          ))}
          {fieldStats.length === 0 && !loading && (
            <p className="text-sm text-muted-foreground text-center py-4">
              No services to audit.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Search */}
      <div className="flex gap-4">
        <div className="flex-1 max-w-sm relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by service name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Badge variant="secondary" className="self-center">
          {filteredResults.length} services
        </Badge>
      </div>

      {/* Service List (sorted by completeness, worst first) */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : filteredResults.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <ClipboardList className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {auditResults.length === 0
                ? 'No services found for this site.'
                : 'No services match the search query.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filteredResults.map((result) => (
            <Card key={result.service.id} className="hover:bg-accent/30 transition-colors">
              <CardContent className="py-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium truncate">{result.service.name}</h3>
                    {result.service.slug && (
                      <p className="text-sm text-muted-foreground truncate">{result.service.slug}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 ml-4">
                    <Progress value={result.completeness} className="h-2 w-24" />
                    <Badge
                      variant={result.completeness === 100 ? 'default' : result.completeness >= 70 ? 'secondary' : 'destructive'}
                    >
                      {result.completeness}%
                    </Badge>
                  </div>
                </div>

                {/* Field checklist */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {result.checks.map((check) => (
                    <div
                      key={check.field}
                      className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded ${
                        check.present
                          ? 'text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-950/30'
                          : 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/30'
                      }`}
                    >
                      {check.present ? (
                        <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
                      ) : (
                        <XCircle className="h-3 w-3 flex-shrink-0" />
                      )}
                      <span className="truncate">{check.label}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
